const { groupForecastRows } = require('../tools/read/to-forecast-per-location')

const stripProductPrefix = (sku) =>
  typeof sku === 'string' && sku.startsWith('product:')
    ? sku.replace('product:', '')
    : sku

const addProductPrefix = (sku) =>
  typeof sku === 'string' && !sku.startsWith('product:')
    ? `product:${sku}`
    : sku

class ForecastDataAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async get (locationId, program, { sku } = {}) {
    if (!locationId) throw new Error('locationId is required')
    if (!program) throw new Error('program is required')

    const conditions = ['location = $1', 'program = $2']
    const params = [locationId, program]
    if (sku) {
      conditions.push(`sku = $${params.push(stripProductPrefix(sku))}`)
    }
    const { rows } = await this.pgConnection.query(`
      SELECT sku, daily_forecast, safety_stock, supply_period_days, lead_time_days, min_level, reorder_point, max_level
      FROM avocado.vw_supply_plan_values
      WHERE ${conditions.join(' AND ')}
    `, params)

    return rows.reduce((acc, row) => {
      const productId = addProductPrefix(row.sku)
      if (acc.products[productId]) {
        console.error(`Warning! multiple forecasts found for sku ${productId}`)
      }
      acc.products[productId] = {
        productId,
        dailyDemand: Number(row.daily_forecast),
        safetyStock: Number(row.safety_stock),
        supplyPeriodDays: Number(row.supply_period_days),
        leadTimeDays: Number(row.lead_time_days),
        minLevel: Number(row.min_level),
        reorderPoint: Number(row.reorder_point),
        maxLevel: Number(row.max_level)
      }
      return acc
    }, { locationId, products: {} })
  }

  // Sums per-product supply-plan values across all of a program's warehouses,
  // grouped by SKU.
  async getAggregated (program) {
    if (!program) throw new Error('program is required')

    const { rows } = await this.pgConnection.query(`
      SELECT
        sku,
        SUM(daily_forecast) AS daily_forecast,
        SUM(safety_stock)   AS safety_stock,
        SUM(reorder_point)  AS reorder_point,
        SUM(max_level)      AS max_level
      FROM avocado.vw_supply_plan_values
      WHERE program = $1
      GROUP BY sku
    `, [program])

    return rows.reduce((acc, row) => {
      acc[addProductPrefix(row.sku)] = {
        dailyDemand: Number(row.daily_forecast),
        minLevel: Number(row.safety_stock),
        reorderPoint: Number(row.reorder_point),
        maxLevel: Number(row.max_level)
      }
      return acc
    }, {})
  }

  async list ({ locationIds, program, sku } = {}) {
    const conditions = []
    const params = []
    if (locationIds && locationIds.length > 0) {
      // Handle the case where a single locationId is passed as a string instead of an array
      const ids = Array.isArray(locationIds) ? locationIds : [locationIds]
      conditions.push(`location = ANY($${params.push(ids)}::text[])`)
    }
    if (program) {
      conditions.push(`program = $${params.push(program)}`)
    }
    if (sku) {
      conditions.push(`sku = $${params.push(stripProductPrefix(sku))}`)
    }
    const where = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : ''
    const { rows } = await this.pgConnection.query(`
      SELECT * FROM avocado.vw_supply_plan_values ${where}
    `, params)
    const prefixedRows = rows.map(row => ({ ...row, sku: addProductPrefix(row.sku) }))
    return groupForecastRows(prefixedRows)
  }
}

module.exports = ForecastDataAdapter
